import React from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  Trophy,
  Award,
  Users,
  BarChart3,
  Zap,
  ChevronRight,
  Play,
  Star,
  Check,
  ArrowRight,
} from 'lucide-react';
import { Button } from '../components/ui/button';

const features = [
  {
    icon: BookOpen,
    title: '10,000+ Professional Courses',
    description: 'Access a vast library of courses across technology, business, leadership, and more.',
  },
  {
    icon: Award,
    title: 'Professional Certificates',
    description: 'Earn industry-recognized certificates to showcase your expertise.',
  },
  {
    icon: Zap,
    title: 'AI-Powered Learning',
    description: 'Interactive whiteboard presentations with AI-generated content and narration.',
  },
  {
    icon: Trophy,
    title: 'Gamified Experience',
    description: 'Earn points, badges, and compete on leaderboards as you learn.',
  },
  {
    icon: Users,
    title: 'Enterprise Ready',
    description: 'Manage teams, track progress, and assign learning paths organization-wide.',
  },
  {
    icon: BarChart3,
    title: 'Advanced Analytics',
    description: 'Detailed insights into learning progress and performance metrics.',
  },
];

const companies = ['Google', 'Microsoft', 'Amazon', 'Meta', 'Apple', 'Netflix'];

const stats = [
  { value: '10,000+', label: 'Professional Courses' },
  { value: '500K+', label: 'Learners Worldwide' },
  { value: '95%', label: 'Completion Rate' },
  { value: '4.8', label: 'Average Rating' },
];

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 bg-[#0056D2] rounded-lg flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading font-bold text-xl text-slate-900">SketchLearn</span>
            </div>
            <nav className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-sm font-medium text-slate-600 hover:text-[#0056D2] transition-colors">Features</a>
              <a href="#courses" className="text-sm font-medium text-slate-600 hover:text-[#0056D2] transition-colors">Courses</a>
              <a href="#enterprise" className="text-sm font-medium text-slate-600 hover:text-[#0056D2] transition-colors">For Enterprise</a>
            </nav>
            <div className="flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" className="text-slate-600 hover:text-[#0056D2]" data-testid="login-btn-header">
                  Log In
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-[#0056D2] hover:bg-[#00419E] text-white font-semibold" data-testid="get-started-btn">
                  Join for Free
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-24 pb-16 lg:pt-32 lg:pb-24 bg-gradient-to-b from-blue-50/50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-up">
              <div className="inline-flex items-center gap-2 bg-blue-100 text-[#0056D2] px-4 py-1.5 rounded-full text-sm font-medium mb-6">
                <Star className="w-4 h-4 fill-[#0056D2]" />
                #1 Corporate Learning Platform
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                Advance your career with{' '}
                <span className="text-[#0056D2]">world-class</span> courses
              </h1>
              <p className="text-lg text-slate-600 mb-8 max-w-lg leading-relaxed">
                Learn from industry experts with our AI-powered interactive courses. 
                Earn professional certificates recognized by top employers worldwide.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button size="lg" className="bg-[#0056D2] hover:bg-[#00419E] text-white font-semibold px-8 h-12 text-base">
                    Start Learning for Free
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/courses">
                  <Button size="lg" variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-50 h-12 text-base">
                    <Play className="w-5 h-5 mr-2" />
                    Explore Courses
                  </Button>
                </Link>
              </div>
              
              {/* Trust Badges */}
              <div className="mt-10 pt-8 border-t border-slate-200">
                <p className="text-sm text-slate-500 mb-4">Trusted by teams at</p>
                <div className="flex flex-wrap items-center gap-6">
                  {companies.map((company) => (
                    <span key={company} className="text-slate-400 font-semibold text-lg">{company}</span>
                  ))}
                </div>
              </div>
            </div>

            {/* Hero Image */}
            <div className="relative animate-fade-in hidden lg:block">
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&h=400&fit=crop"
                  alt="Professional learning"
                  className="rounded-2xl shadow-2xl"
                />
                {/* Floating Card */}
                <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-4 border border-slate-100">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <Check className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">Certificate Earned</p>
                      <p className="text-sm text-slate-500">Professional Leadership</p>
                    </div>
                  </div>
                </div>
                {/* Stats Card */}
                <div className="absolute -top-4 -right-4 bg-white rounded-xl shadow-xl p-4 border border-slate-100">
                  <p className="text-2xl font-bold text-[#0056D2]">10,000+</p>
                  <p className="text-sm text-slate-500">Professional Courses</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#0056D2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <p className="text-3xl md:text-4xl font-bold text-white mb-2">{stat.value}</p>
                <p className="text-blue-200 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Everything you need to succeed
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Our platform combines cutting-edge AI technology with proven learning methodologies
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={feature.title}
                  className="bg-white p-6 rounded-xl border border-slate-200 hover:border-blue-200 hover:shadow-lg transition-all duration-300 animate-slide-up"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-[#0056D2]" />
                  </div>
                  <h3 className="font-semibold text-lg text-slate-900 mb-2">{feature.title}</h3>
                  <p className="text-slate-600">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Start your learning journey today
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join thousands of professionals advancing their careers with SketchLearn
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" className="bg-white text-[#0056D2] hover:bg-blue-50 font-semibold px-8 h-12">
                Get Started Free
                <ChevronRight className="w-5 h-5 ml-1" />
              </Button>
            </Link>
            <Link to="/login">
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 h-12">
                Sign In
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#0056D2] rounded-lg flex items-center justify-center">
                <BookOpen className="w-4 h-4 text-white" />
              </div>
              <span className="font-heading font-bold text-lg text-white">SketchLearn</span>
            </div>
            <p className="text-sm">© 2025 SketchLearn. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
